import { I18N, React, Theme, variantProvider } from '@/app'
import { ActionIcon, Button, CategoryExplainer, Image, TextInput, View } from '@/components'
import { Organisation } from '@/features'
import { analytics } from '@/services/analytics/segment'
import { CategoryData } from '@/types'
import { isValidImageUrl, useIsMobile, useModal } from '@/utils'
import { onUpdate, TypeGuards, useEffect, useRef, useState, useValidate } from '@codeleap/common'
import { modal } from '@codeleap/modals'
import { validatePulseComment } from '../utils'
import { PulsePage } from './Page'

const ReviewOrganisationContent = () => {
  const categoryModal = useModal()
  const [category, setCategory] = useState<CategoryData>(null)

  const footerRef = useRef<HTMLDivElement>(null)
  const [footerHeight, setFooterHeight] = useState<number | null>(null)

  const questionsAnswered = useRef<number[]>([])
  const answersComment = useRef<{ id: number; comment?: string }[]>([])

  const pulseQuestions = Organisation.Pulse.usePulseQuestions()
  const answers = Organisation.Pulse.usePulseAnswers()
  const nav = Organisation.Pulse.usePulseNavigation(pulseQuestions.questions.length)
  const flow = Organisation.Pulse.usePulseFlow({
    questions: pulseQuestions.questions,
    answers: answers.answers,
    clearAnswers: answers.clearAnswers
  })

  const { questions, available, from } = pulseQuestions
  const { page, answer, setAnswer, clearAnswers } = answers
  const { isFirst, isLast, onBack } = nav

  const currentQuestion = questions[page]

  onUpdate(() => {
    if (footerRef.current && !footerHeight) {
      setFooterHeight(footerRef.current.clientHeight)
    }
  }, [footerRef.current])

  const onAnswer = () => {
    if (isLast) {
      flow.finish()
    } else {
      nav.next()
    }
  }

  const skip = () => {
    setAnswer({ skip: true, value: null, comment: null })
    onAnswer()
  }

  const onSkip = () => {
    analytics.track('review_skip')
    skip()
  }

  const onNext = () => {
    if (!questionsAnswered.current.includes(currentQuestion.id)) {
      analytics.track('review_add', {
        source_from: from,
        question_type: currentQuestion.type
      })
      questionsAnswered.current.push(currentQuestion.id)
    }

    const commentChanged = answersComment.current.find(c => c.id === currentQuestion.id)?.comment !== answer.comment

    if (answer.comment && commentChanged) {
      analytics.track('question_answer')
      answersComment.current.push({
        id: currentQuestion.id,
        comment: answer.comment
      })
    }

    onAnswer()
  }

  const onClose = () => {
    questionsAnswered.current = []
    answersComment.current = []
    ReviewOrganisationModal.close()

    setTimeout(() => {
      clearAnswers()
    }, 250)
  }

  const onCategoryPress = (c: CategoryData) => {
    setCategory(c)
    categoryModal.toggle(true)
  }

  const isMobile = useIsMobile()

  const hasAnswer = TypeGuards.isNumber(answer?.value) || TypeGuards.isNumber(answer?.value?.[0])

  const hasComment = !!answer?.comment?.trim()

  const commentValidation = useValidate(answer?.comment ?? '', validatePulseComment)

  const isCommentValid = validatePulseComment(answer?.comment ?? '').valid

  const isValid = hasAnswer && (!hasComment || isCommentValid)

  const [isImageValid, setIsImageValid] = useState(false)

  useEffect(() => {
    if (!available) return

    if (currentQuestion?.web_image) {
      isValidImageUrl(currentQuestion.web_image).then(setIsImageValid)
    } else {
      setIsImageValid(false)
    }
  }, [currentQuestion?.web_image, available])

  const renderIndicator = React.useCallback(
    (_, i) => {
      const _color = page === i ? Theme.colors.light.neutral9 : Theme.colors.light.neutral3

      return <View key={'indicator-' + i} css={[styles.indicator, { background: _color }]} />
    },
    [page]
  )

  const Footer = (
    <>
      <View style={styles.footerWrapper} ref={footerRef}>
        <View variants={['row', 'justifySpaceBetween']} responsiveVariants={{ small: ['gap:2', 'column'] }}>
          <Button
            debugName="Back button on Pulse questions"
            text={isMobile ? I18N.t(isLast ? 'submit' : 'next') : I18N.t('back')}
            variants={[!isMobile && 'outline', 'large', 'pill', 'paddingHorizontal:8']}
            onPress={isMobile ? onNext : onBack}
            disabled={isMobile ? !isValid : isFirst}
          />

          <View variants={['row', 'center', 'gap:2']} responsiveVariants={{ small: ['rowReverse', 'fullWidth'] }}>
            <Button
              debugName="Skip button on Pulse questions"
              text={I18N.t('skip')}
              variants={['outline', 'large', 'pill', 'paddingHorizontal:8']}
              responsiveVariants={{ small: ['fullWidth'] }}
              disabled={!currentQuestion?.skippable}
              onPress={onSkip}
            />

            <Button
              debugName="Next button on Pulse questions"
              text={!isMobile ? I18N.t(isLast ? 'submit' : 'next') : I18N.t('back')}
              variants={[isMobile && 'outline', 'large', 'pill', 'paddingHorizontal:8']}
              onPress={!isMobile ? onNext : onBack}
              disabled={!isValid}
            />
          </View>
        </View>

        <View variants={['fullWidth', 'row', 'gap:1', 'center']}>{questions?.map(renderIndicator)}</View>
      </View>
    </>
  )

  if (!available || !currentQuestion) return null

  return (
    <>
      <View style={styles.container}>
        <ActionIcon
          onPress={onClose}
          icon="x"
          variants={['normalize', 'minimal', 'absolute', 'iconSize:4']}
          style={styles.close}
          debugName="Close button on Pulse questions"
        />
        {isImageValid && <Image source={currentQuestion.web_image} style={styles.image} />}

        <PulsePage
          key={currentQuestion.id}
          question={currentQuestion}
          answer={answer}
          setAnswer={setAnswer}
          footerHeight={footerHeight}
          onCategoryPress={onCategoryPress}
          commentInput={
            <TextInput
              placeholder={I18N.t('pulseQuestion.placeholder.feedback')}
              value={answer?.comment ?? ''}
              onChangeText={comment => setAnswer({ comment })}
              onBlur={commentValidation.onInputBlurred}
              onFocus={commentValidation.onInputFocused}
              _error={commentValidation.showError ? commentValidation.message : ''}
            />
          }
        />
      </View>

      {Footer}

      <CategoryExplainer visible={categoryModal.visible} toggle={categoryModal.toggle} category={category} />
    </>
  )
}

const MODAL_WIDTH_DESKTOP = 720
const IMAGE_HEIGHT = 200

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      maxHeight: '90svh',
      [theme.media.down('small')]: {
        height: '100dvh',
        maxHeight: '100dvh',
        width: '100vw',
        overflowY: 'hidden'
      }
    },

    box: {
      ...theme.spacing.padding(0),
      backgroundColor: theme.colors.neutral1,
      width: MODAL_WIDTH_DESKTOP,
      maxWidth: `calc(100vw - ${theme.spacing.value(4)}px)`,
      borderRadius: theme.borderRadius.medium,

      [theme.media.down('small')]: {
        width: '100vw',
        maxWidth: '100vw',
        height: '100dvh',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column'
      }
    },

    body: {
      ...theme.spacing.padding(0),
      width: '100%',
      maxHeight: '90svh',
      overflowY: 'auto',
      [theme.media.down('small')]: {
        height: '100dvh',
        maxHeight: '100dvh',
        overflowY: 'hidden'
      }
    },

    header: {
      ...theme.spacing.marginBottom(0)
    },
    container: {
      ...theme.presets.column,
      ...theme.spacing.paddingTop(6),
      flex: 1
    },
    close: {
      zIndex: 2,
      top: theme.spacing.value(2),
      right: theme.spacing.value(4),

      [theme.media.down('small')]: {
        top: theme.spacing.value(2),
        right: theme.spacing.value(2)
      }
    },
    image: {
      width: '100%',
      height: IMAGE_HEIGHT,
      objectFit: 'cover',

      [theme.media.down('small')]: {
        display: 'none'
      }
    },
    footer: {
      [theme.media.down('small')]: {
        ...theme.spacing.paddingBottom(2)
      }
    },
    footerWrapper: {
      ...theme.presets.column,
      ...theme.presets.fullWidth,
      ...theme.spacing.padding(4),
      ...theme.spacing.paddingTop(0),
      ...theme.spacing.gap(2),
      backgroundColor: theme.colors.neutral1,

      [theme.media.down('small')]: {
        ...theme.presets.column,
        ...theme.spacing.padding(2),
        ...theme.spacing.paddingTop(0),
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0
      }
    },
    indicator: {
      ...theme.sized(1),
      borderRadius: theme.borderRadius.rounded
    }
  }),
  true
)

export const ReviewOrganisationModal = modal()
  .content(p => <ReviewOrganisationContent />)
  .props({
    variants: ['centered'],
    showClose: false,
    styles: {
      wrapper: styles.wrapper,
      box: styles.box,
      body: styles.body,
      header: styles.header
    }
  })
